import { reportCallBack } from '~/api/system'

export function classNames(...classes: any[string]) {
  return classes.filter(Boolean).join(' ')
}

export const kebabCase = (str: string) => {
  return str
    ?.match(
      /[A-Z]{2,}(?=[A-Z][a-z]+[0-9]*|\b)|[A-Z]?[a-z]+[0-9]*|[A-Z]|[0-9]+/g,
    )
    ?.map((x) => x.toLowerCase())
    ?.join('-')
}

export const omit = (obj: object, keys: string[]) => {
  return Object.fromEntries(
    Object.entries(obj).filter(([key]) => !keys.includes(key)),
  )
}

const hashRE = /#.*$/
const extRE = /(index)?\.(md|html)$/
const endingSlashRE = /\/$/

/**
 * Remove `.md` or `.html` extention from the given path. It also converts
 * `index` to slush.
 */
export function normalize(path: string): string {
  return ensureStartingSlash(
    decodeURI(path)
      .replace(hashRE, '')
      .replace(extRE, '')
      .replace(endingSlashRE, ''),
  )
}

function ensureStartingSlash(path: string): string {
  return path.startsWith('/') ? path : `/${path}`
}

export function joinUrl(base: string, path: string): string {
  if (path.startsWith('#')) return path
  return `${base}${path.startsWith('/') ? path.slice(1) : path}`
}

export function getCnOrEn(cn: string, en: string) {
  const { locale, defaultLocale } = useI18n()

  if (locale.value) {
    return locale.value === 'zh-CN' ? cn : en
  }
  return defaultLocale === 'zh-CN' ? cn : en
}

/**
 * 判断域名是否为IP
 * @param {*} hostname
 * @returns
 */
export function isIpAddress(hostname: string) {
  const ipv4Pattern =
    /^(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/
  const ipv6Pattern =
    /^((?:[a-fA-F0-9]{1,4}:){7}[a-fA-F0-9]{1,4}|::|(?:[a-fA-F0-9]{1,4}:){1,7}:|:(?::[a-fA-F0-9]{1,4}){1,7})$/

  return ipv4Pattern.test(hostname) || ipv6Pattern.test(hostname)
}

/**
 * 获取一级域名
 * @returns
 */
export function getFirstDomain() {
  if (typeof window !== 'undefined') {
    // 确保只在客户端执行
    const { hostname } = window.location
    const parts = hostname.split('.')
    if (parts.length < 3 || isIpAddress(hostname)) return null
    return parts.length === 3
      ? `.${parts.slice(-2).join('.')}`
      : `.${parts.slice(-3).join('.')}`
  }
  return null
}

export function splitArrayIntoGroups(arr: any[], groupSize: number) {
  const result = []
  if (arr && arr.length) {
    for (let i = 0; i < arr.length; i += groupSize) {
      result.push(arr.slice(i, i + groupSize))
    }
  }

  return result
}

export function getVipLogoClass(code: string) {
  const map: any = {
    'JC eCommerce': 'vip-code-4',
    'JC Projects': 'vip-code-7',
    'JC Dangerous Goods': 'vip-code-2',
    GRP: 'vip-code-5',
    GCP: 'vip-code-3',
    'JC Rising': 'vip-code-5',
    'JC Premium': 'vip-code-3',
    'JC Railway': 'vip-code-6',
    'JC Elite': 'vip-code-1',
    'JC Vendor': 'vip-code-8', // JC Vendor
    'JC Vendor Consulting Services': 'vip-code-8-1', // JC Vendor Consulting Services
    'JC Vendor Finance Services': 'vip-code-8-2', // JC Vendor Finance Services
    'JC Vendor IT Services': 'vip-code-8-3', // JC Vendor IT Services
    'JC Vendor Transportation': 'vip-code-8-4', // JC Vendor Transportation
    'JC Vendor Warehousing': 'vip-code-8-5', // JC Vendor Warehousing
  }
  return map[code] || ''
}

export const isProd = () => {
  return process.env.NODE_ENV === 'production'
}

export const isDev = () => {
  return process.env.NODE_ENV === 'development'
}

export async function openGlobalIM(item: any = {}) {
  console.log('🚀 ~ file: index.ts:128 ~ openGlobalIM ~ item:', item)
}

// 是否是 URL
export const isURL = (url = '') => url.indexOf('http') === 0

// 参考 https://cn.vitejs.dev/guide/assets.html#new-url-url-import-meta-url
/**
 * @description: 动态加载图片
 * @param {String} url 图片 url 地址
 * @return {String}
 */
export const getImageSrc = (url = '') => {
  // 区分本地和网络
  if (url === '') {
    return ''
  }
  if (isURL(url)) {
    return url
  }
  return new URL(
    `../assets/images/${url.replace(/^\//, '')}`,
    // `../assets/images/${url
    //   .split('/')
    //   .filter((i) => i)
    //   .join('/')}`, // 去除开头的 /
    import.meta.url,
  ).href
}

export function generateLengthRegex(
  min: number | string,
  max: number | string,
) {
  const minLength = Number(min)
  const maxLength = Number(max)
  if (minLength < 0 || maxLength < 0 || minLength > maxLength) {
    throw new Error('Invalid input values')
  }

  const patternString = `^.{$minLength,$maxLength}$`
    .replace(/\$minLength/g, String(minLength))
    .replace(/\$maxLength/g, String(maxLength))
  return new RegExp(patternString)
}

export function getText(str = '') {
  const words = str?.replace(/<[^<>]+>/g, '')?.replace(/&nbsp;/gi, '')
  return words?.replace(/\s{2,}/g, ' ')
}

export function filterContent(data: string, limit = 130) {
  // 内容过滤
  return getText(data)?.slice(0, limit)
}

export const downFile = (fileUrl: string) => {
  if (typeof window !== 'undefined') {
    const link = document.createElement('a')
    link.target = '$gateway_view_download'
    link.href = fileUrl || ''
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }
}
